/**
 * useSmartSearch — one debounced search that answers with eras, sets and cards.
 *
 * The owner's rule is that every search bar in the app is smart, so this is
 * deliberately a hook and not a screen. Give it a query string and the era and
 * set lists you already hold, and it gives back three labelled groups. It makes
 * no assumptions about how they are drawn.
 *
 * Division of labour:
 *   eras (13) and sets (213) are already in memory, so they are matched here
 *   and cost nothing; cards (23,591) are matched in Postgres against the
 *   trigram and local_id indexes.
 *
 * SORT AND FILTER LIVE HERE TOO, and both are pushed into the query rather than
 * applied to the rows. Sixty rows re-ordered in the browser are a page in order
 * and a lie about the rest of the match. The one thing that is NOT re-queried is
 * the facet set: which rarities and sets and eras a query matches depends on the
 * query alone, so changing the order or ticking a chip reuses the tier and the
 * facets already resolved and costs exactly one request.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { searchCatalog } from '../services/smartCatalogSearch';
import { warmCatalogIndex, searchCatalogIndex } from '../services/localCatalogIndex';
import { SEARCH_RELEVANCE_SORT, hasCatalogFilters } from '../services/cardData/catalogFilters';
import { parseQuery, isSearchable, matchEras, matchSets } from '../utils/smartSearch';

const DEBOUNCE_MS = 250;

/**
 * @param {string} query
 * @param {{
 *   eras?: object[], sets?: object[], limit?: number,
 *   enabled?: boolean, kinds?: ('era'|'set'|'card')[],
 *   sort?: string|null, filters?: object|null,
 * }} [options]
 */
export default function useSmartSearch(query, {
  eras = [],
  sets = [],
  limit = 60,
  enabled = true,
  kinds = ['era', 'set', 'card'],
  sort = null,
  filters = null,
  // Whether the facets are wanted right now. They describe the whole match for
  // the filter sheet, and they cost a third query on every keystroke, so a
  // caller passes true only while the sheet is open. A ticked filter needs them
  // regardless, because they decide which tier answers; that case is handled
  // inside searchCatalog and does not depend on this.
  facets: wantFacets = true,
} = {}) {
  const parsed = useMemo(() => parseQuery(query), [query]);
  const active = enabled && isSearchable(parsed);

  const wantEras = kinds.includes('era');
  const wantSets = kinds.includes('set');
  const wantCards = kinds.includes('card');

  const eraHits = useMemo(
    () => (active && wantEras ? matchEras(parsed, eras) : []),
    [active, wantEras, parsed, eras],
  );
  const setHits = useMemo(
    () => (active && wantSets ? matchSets(parsed, sets) : []),
    [active, wantSets, parsed, sets],
  );

  const [cardHits, setCardHits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // What the whole match contains, for fading the filter choices it has none
  // of. `null` means not answered yet, which must fade nothing.
  const [facets, setFacets] = useState(null);

  const latest = useRef(0);
  // The tier the current query resolved to, plus its facets, so a sort or a
  // filter change does not re-run the probe. Keyed on the query itself: the
  // moment the typed text changes, both are stale and are thrown away.
  const resolved = useRef({ key: null, match: null, facets: null });
  // The last text the instant preview answered. Separate from `resolved` on
  // purpose: that one records what the DATABASE last finished, and typing
  // "char 4", then "char", then "char 4" again before the middle answer landed
  // made the third look like a repeat of the first. The preview was skipped,
  // the rows for "char" stayed on screen under "char 4", and on a slow link
  // they stayed there for as long as the database took. The preview follows
  // the text in the box, nothing else.
  const previewed = useRef(null);

  // The identity of the QUERY, as opposed to the identity of the request. Sort
  // and filter are deliberately absent from it.
  const queryKey = `${parsed.flat}|${[...parsed.localIds].sort().join(',')}`;
  const filtersKey = JSON.stringify(filters || null);
  // True while a re-query is in flight for a query whose results are already on
  // screen — a sort or a filter change. The screen keeps the cards it has and
  // says what it is doing, rather than blanking and coming back.
  const [reordering, setReordering] = useState(false);

  useEffect(() => {
    if (!active || !wantCards) {
      setCardHits([]);
      setLoading(false);
      setReordering(false);
      setError(null);
      setFacets(null);
      resolved.current = { key: null, match: null, facets: null };
      return undefined;
    }

    let alive = true;
    const run = ++latest.current;
    const sameQuery = resolved.current.key === queryKey;
    const textChanged = previewed.current !== queryKey;
    setLoading(true);
    // A re-sort is the same text asked again in a different order. Changed
    // text is a new search, however recently the database last saw it.
    if (sameQuery && !textChanged) setReordering(true);
    setError(null);

    // The instant answer, from the catalogue in memory, before the debounce
    // and the round trip. Whenever the text changes, in relevance order with
    // nothing ticked: under a sort or a filter the database's order is the
    // whole point, and a preview in a different order would be a lie for
    // 250ms. The rows that land below replace these, pictured and priced.
    if (textChanged) {
      previewed.current = queryKey;
      if ((!sort || sort === SEARCH_RELEVANCE_SORT) && !hasCatalogFilters(filters)) {
        const instant = searchCatalogIndex(parsed, { sets, limit });
        if (instant === null) warmCatalogIndex();
        else setCardHits(instant);
      }
    }

    const timer = setTimeout(async () => {
      try {
        const result = await searchCatalog(parsed, {
          limit,
          sets,
          sort,
          filters,
          match: sameQuery ? resolved.current.match : null,
          // Wanted, and not already in hand for this query. Opening the sheet
          // on a query typed with it closed is the case the second clause is
          // for: same text, no facets yet.
          facets: wantFacets && (!sameQuery || !resolved.current.facets),
        });
        if (!alive || run !== latest.current) return;
        setCardHits(result.rows);
        if (sameQuery) {
          // The facets belong to the query, and the query did not change.
          if (result.facets) resolved.current.facets = result.facets;
          setFacets(resolved.current.facets);
        } else {
          resolved.current = { key: queryKey, match: result.match, facets: result.facets };
          setFacets(result.facets);
        }
      } catch (e) {
        if (!alive || run !== latest.current) return;
        console.error('[smartSearch] card query failed:', e?.message || e);
        setCardHits([]);
        setError(e);
      } finally {
        if (alive && run === latest.current) {
          setLoading(false);
          setReordering(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, wantCards, parsed, limit, sort, filtersKey, wantFacets]);

  const total = eraHits.length + setHits.length + cardHits.length;

  return {
    parsed,
    active,
    loading,
    /** A re-query over results already on screen: a sort or a filter change. */
    reordering,
    error,
    eraHits,
    setHits,
    cardHits,
    /**
     * What the whole card match contains — types, rarities, eras, kinds, sets —
     * for fading the choices it has none of. `null` until answered, and
     * `capped: true` when the match was too large to count honestly; both mean
     * fade nothing.
     */
    facets,
    /** How many cards the query matches in total, not how many are on screen. */
    matchTotal: facets && !facets.capped ? facets.total : null,
    total,
    /** True only once every source has answered and none of them had anything. */
    empty: active && !loading && total === 0,
  };
}
